import React, { useEffect, useMemo, useState } from "react";
import TopNav from "../components/TopNav";
import AppTile from "../components/AppTile";
import Announcements from "../components/Announcements";
import { appsApi, favApi, launchApi, annApi } from "../lib/api";
import { Search, History, Star } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Dashboard() {
    const { user } = useAuth();
    const [apps, setApps] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [recent, setRecent] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);

    const loadAll = async () => {
        try {
            const [a, f, r, n] = await Promise.all([
                appsApi.list(),
                favApi.list(),
                launchApi.recent(),
                annApi.list(),
            ]);
            setApps(a);
            setFavorites(f.app_ids || []);
            setRecent(r.app_ids || []);
            setAnnouncements(n);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const appMap = useMemo(() => {
        const m = {};
        apps.forEach((a) => {
            m[a.id] = a;
        });
        return m;
    }, [apps]);

    const favoriteApps = favorites
        .map((id) => appMap[id])
        .filter(Boolean);
    const recentApps = recent.map((id) => appMap[id]).filter(Boolean);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return apps;
        return apps.filter(
            (a) =>
                a.name.toLowerCase().includes(q) ||
                (a.category || "").toLowerCase().includes(q) ||
                (a.description || "").toLowerCase().includes(q),
        );
    }, [apps, query]);

    const launch = async (app) => {
        window.open(app.url, "_blank", "noopener,noreferrer");
        try {
            await launchApi.record(app.id);
            const r = await launchApi.recent();
            setRecent(r.app_ids || []);
        } catch {
            // ignore
        }
    };

    const toggleFav = async (app) => {
        const isFav = favorites.includes(app.id);
        try {
            const res = isFav
                ? await favApi.remove(app.id)
                : await favApi.add(app.id);
            setFavorites(res.app_ids || []);
        } catch {
            // ignore
        }
    };

    return (
        <div className="min-h-screen" data-testid="dashboard-page">
            <TopNav />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
                {/* Hero */}
                <div className="mb-8 sm:mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                    <div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none">
                            HEY,{" "}
                            <span className="bg-[#E8241A] text-white px-2 inline-block">
                                {(user?.first_name || user?.email || "").toUpperCase()}
                            </span>
                        </h1>
                        <p className="mt-3 text-sm sm:text-base opacity-80 max-w-xl">
                            Everything you need — one click away. Launch, pin
                            favorites, and jump back to recent apps.
                        </p>
                    </div>
                    <div className="relative w-full md:w-80">
                        <Search
                            size={18}
                            strokeWidth={3}
                            className="absolute left-3 top-1/2 -translate-y-1/2"
                        />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search apps…"
                            className="w-full pl-10 pr-4 py-3 brut-border brut-shadow-sm bg-white outline-none font-mono"
                            data-testid="dashboard-search-input"
                        />
                    </div>
                </div>

                <Announcements items={announcements} />

                {/* Favorites */}
                {favoriteApps.length > 0 && (
                    <Section
                        title="Pinned"
                        icon={<Star size={18} strokeWidth={3} />}
                        testId="favorites-section"
                    >
                        <Grid>
                            {favoriteApps.map((a, idx) => (
                                <AppTile
                                    key={a.id}
                                    app={a}
                                    favorite
                                    onLaunch={launch}
                                    onToggleFavorite={toggleFav}
                                    rotation={idx % 2 === 0 ? -1 : 1}
                                />
                            ))}
                        </Grid>
                    </Section>
                )}

                {/* Recently used */}
                {recentApps.length > 0 && (
                    <Section
                        title="Recently Used"
                        icon={<History size={18} strokeWidth={3} />}
                        testId="recent-section"
                    >
                        <Grid>
                            {recentApps.map((a) => (
                                <AppTile
                                    key={"r-" + a.id}
                                    app={a}
                                    favorite={favorites.includes(a.id)}
                                    onLaunch={launch}
                                    onToggleFavorite={toggleFav}
                                />
                            ))}
                        </Grid>
                    </Section>
                )}

                {/* All apps */}
                <Section title="All Apps" testId="all-apps-section">
                    {loading ? (
                        <div className="font-display text-lg opacity-50">
                            LOADING…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="brut-border bg-white p-8 font-display text-lg">
                            No apps match “{query}”.
                        </div>
                    ) : (
                        <Grid>
                            {filtered.map((a, idx) => (
                                <AppTile
                                    key={a.id}
                                    app={a}
                                    favorite={favorites.includes(a.id)}
                                    onLaunch={launch}
                                    onToggleFavorite={toggleFav}
                                    rotation={
                                        idx % 7 === 0
                                            ? -1
                                            : idx % 11 === 0
                                              ? 1
                                              : 0
                                    }
                                />
                            ))}
                        </Grid>
                    )}
                </Section>
            </main>
        </div>
    );
}

function Section({ title, icon, children, testId }) {
    return (
        <section className="mb-10 sm:mb-12" data-testid={testId}>
            <div className="flex items-center gap-2 mb-4">
                <span className="bg-black text-white brut-border inline-flex items-center gap-2 px-3 py-1.5">
                    {icon}
                    <h2 className="font-display text-sm sm:text-base">
                        {title}
                    </h2>
                </span>
                <span className="flex-1 h-[3px] bg-black" />
            </div>
            {children}
        </section>
    );
}

function Grid({ children }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {children}
        </div>
    );
}
